import { useEffect, useMemo, useState } from "react";
import { adjustmentSchema, type SceneNode } from "@proof/core";
import {
  checkedPreviewBlob,
  imageNeedsTransform,
  imagePreviewKey,
  httpImageTransformer,
  type ImageTransformer,
  type AssetLoader,
} from "./images";
export interface ImagePreviewStatus {
  ready: boolean;
  error?: string;
}
export function ImageLayer({
  node,
  assetUrl,
  transformImage,
  loadAsset,
  revision,
  onStatus,
  alt,
  className,
}: {
  node: Pick<SceneNode, "id" | "assetId" | "adjustments" | "width" | "height">;
  alt?: string;
  className?: string;
  assetUrl: (id: string) => string;
  transformImage?: ImageTransformer;
  loadAsset?: AssetLoader;
  revision: object;
  onStatus: (id: string, signature: string, status: ImagePreviewStatus) => void;
}) {
  const transformer = useMemo(
      () => transformImage || httpImageTransformer(assetUrl),
      [transformImage, assetUrl],
    ),
    adjusted = imageNeedsTransform(node.adjustments),
    signature = imagePreviewKey(node),
    key = useMemo(() => ({}), [signature, revision, transformer, loadAsset]);
  const [state, setState] = useState<{
    key: object;
    url?: string;
    error?: string;
  }>();
  useEffect(() => {
    if (!node.assetId) return;
    let live = true,
      url: string | undefined,
      owned = false;
    const controller = new AbortController();
    onStatus(node.id, signature, { ready: false });
    void Promise.resolve()
      .then(async () => {
        if (adjusted || loadAsset) {
          const blob = adjusted
            ? await transformer(
                node.assetId!,
                adjustmentSchema.parse(node.adjustments || {}),
                controller.signal,
              )
            : await loadAsset!(node.assetId!, controller.signal);
          if (!live) return;
          checkedPreviewBlob(blob);
          url = URL.createObjectURL(blob);
          owned = true;
        } else url = assetUrl(node.assetId!);
        const image = new Image();
        image.src = url;
        await image.decode();
        if (!live) return;
        if (
          !image.naturalWidth ||
          !image.naturalHeight ||
          image.naturalWidth * image.naturalHeight > 40000000
        )
          throw new Error("IMAGE_PREVIEW_INVALID");
        setState({ key, url });
        onStatus(node.id, signature, { ready: true });
      })
      .catch(() => {
        if (owned && url) {
          URL.revokeObjectURL(url);
          url = undefined;
        }
        if (!live) return;
        setState({ key, error: "IMAGE_PREVIEW_UNAVAILABLE" });
        onStatus(node.id, signature, {
          ready: false,
          error: "IMAGE_PREVIEW_UNAVAILABLE",
        });
      });
    return () => {
      live = false;
      controller.abort();
      if (owned && url) URL.revokeObjectURL(url);
    };
  }, [key, onStatus]);
  if (!node.assetId) return null;
  const current = state?.key === key ? state : undefined;
  if (alt !== undefined)
    return current?.url ? (
      <img src={current.url} alt={alt} className={className} />
    ) : (
      <span role={current?.error ? "alert" : "status"} className={className}>
        {current?.error
          ? `${alt}: image preview unavailable`
          : `Preparing ${alt}`}
      </span>
    );
  return current?.url ? (
    <image
      href={current.url}
      width={node.width}
      height={node.height}
      preserveAspectRatio="xMidYMid meet"
      data-image-state="ready"
    />
  ) : (
    <g data-image-state={current?.error ? "error" : "loading"}>
      <title>
        {current?.error
          ? "Image preview unavailable"
          : "Preparing image preview"}
      </title>
      <rect
        width={node.width}
        height={node.height}
        fill="#f0f2ee"
        stroke="#aab5ad"
        strokeDasharray="4 3"
      />
    </g>
  );
}
